import { useAdminFlowCanvas } from "../../hooks/useAdminFlowCanvas";
import type { AdminFlowWhiteboardProps } from "../../models/admin-flow-whiteboard.models";
import {
  ADMIN_FLOW_CANVAS_MIN_HEIGHT,
  ADMIN_FLOW_CANVAS_MIN_WIDTH,
  ADMIN_FLOW_CARD_HEIGHT,
  ADMIN_FLOW_CARD_WIDTH,
  ADMIN_FLOW_KIND_COLORS,
  ADMIN_FLOW_KIND_LABELS,
  ADMIN_FLOW_OUTPUT_OPTIONS,
  ADMIN_FLOW_SECONDARY_BUTTON_CLASS,
  ADMIN_FLOW_SOURCE_OPTIONS,
  ADMIN_FLOW_STEP_OPTIONS,
} from "../../utils/admin-flow-whiteboard.constants";
import { getAdminFlowToolNames } from "../../utils/admin-flows.utils";
import AdminFlowBlockSettings from "./AdminFlowBlockSettings";
import AdminFlowToolMenu from "./AdminFlowToolMenu";
import { useId, useState } from "react";

// Present the page-owned flow as movable blocks with directed connections.
// Node settings and graph edits are shared with Cards; zoom and open drawers belong only to this view.
const AdminFlowWhiteboard = ({ flow, tools, onChange, fillHeight = false }: AdminFlowWhiteboardProps) => {
  const canvas = useAdminFlowCanvas({ flow, onChange });
  const [showPalette, setShowPalette] = useState(true);
  const [showSettings, setShowSettings] = useState(true);
  const markerId = `admin-flow-arrow-${useId().replace(/:/g, "")}`;
  const width = Math.max(
    ADMIN_FLOW_CANVAS_MIN_WIDTH,
    ...flow.nodes.map((node) => node.x + ADMIN_FLOW_CARD_WIDTH + 100),
  );
  const height = Math.max(
    ADMIN_FLOW_CANVAS_MIN_HEIGHT,
    ...flow.nodes.map((node) => node.y + ADMIN_FLOW_CARD_HEIGHT + 100),
  );
  const nodeIndex = new Map(flow.nodes.map((node) => [node.id, node]));
  const selectedConnections = flow.edges.filter(
    (edge) => edge.from === canvas.selectedNode?.id || edge.to === canvas.selectedNode?.id,
  );
  const connectingNode = flow.nodes.find((node) => node.id === canvas.connectingFrom);

  return (
    <section
      data-test="admin-flow-whiteboard"
      className={fillHeight ? "flex h-full min-h-0 min-w-0 flex-col gap-2" : "min-w-0 space-y-2"}
    >
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          <button
            type="button"
            data-test="admin-flow-toggle-palette"
            aria-expanded={showPalette}
            onClick={() => setShowPalette((value) => !value)}
            className={ADMIN_FLOW_SECONDARY_BUTTON_CLASS}
          >
            Building blocks
          </button>
          <button
            type="button"
            data-test="admin-flow-toggle-settings"
            aria-expanded={showSettings}
            onClick={() => setShowSettings((value) => !value)}
            className={ADMIN_FLOW_SECONDARY_BUTTON_CLASS}
          >
            Block settings
          </button>
          <details className="relative self-center" data-test="admin-flow-canvas-help">
            <summary className="cursor-pointer px-2 text-xs text-gray-500">Help</summary>
            <p className="absolute left-0 top-full z-30 mt-2 w-64 rounded border border-gray-200 bg-white p-3 text-xs leading-5 shadow-lg dark:border-gray-700 dark:bg-gray-800">
              Add blocks, then drag an output port to an input port, or select the ports in order. Drag blocks to
              arrange them; use arrow keys on a block title to move it. Scroll to zoom; hold Space and drag to pan.
            </p>
          </details>
        </div>
        <div className="flex items-center gap-2" role="group" aria-label="Canvas zoom">
          <button
            type="button"
            data-test="admin-flow-zoom-out"
            aria-label="Zoom out"
            onClick={() => canvas.changeZoom(canvas.zoom - 0.15)}
            className={ADMIN_FLOW_SECONDARY_BUTTON_CLASS}
          >
            −
          </button>
          <span data-test="admin-flow-zoom-level" className="w-12 text-center text-xs tabular-nums">
            {Math.round(canvas.zoom * 100)}%
          </span>
          <button
            type="button"
            data-test="admin-flow-zoom-in"
            aria-label="Zoom in"
            onClick={() => canvas.changeZoom(canvas.zoom + 0.15)}
            className={ADMIN_FLOW_SECONDARY_BUTTON_CLASS}
          >
            +
          </button>
          <button
            type="button"
            data-test="admin-flow-fit-canvas"
            onClick={canvas.fitCanvas}
            className={ADMIN_FLOW_SECONDARY_BUTTON_CLASS}
          >
            Fit flow
          </button>
        </div>
      </div>
      {canvas.error && (
        <p
          data-test="admin-flow-connection-error"
          role="alert"
          className="shrink-0 rounded border border-red-200 bg-red-50 px-3 py-1 text-xs text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
        >
          {canvas.error}
        </p>
      )}
      {connectingNode && (
        <div
          data-test="admin-flow-connection-prompt"
          role="status"
          className="flex shrink-0 items-center justify-between gap-3 rounded bg-[#E9F4F3] px-3 py-1 text-xs text-green dark:bg-green/10"
        >
          <span>
            Connect from {connectingNode.label || ADMIN_FLOW_KIND_LABELS[connectingNode.kind]}: choose another
            block&apos;s input.
          </span>
          <button
            type="button"
            data-test="admin-flow-cancel-connection"
            onClick={() => canvas.setConnectingFrom(undefined)}
            className="font-semibold underline"
          >
            Cancel
          </button>
        </div>
      )}
      <div
        className={
          fillHeight
            ? "relative flex min-h-0 min-w-0 flex-1 items-stretch gap-3"
            : "flex min-w-0 flex-wrap items-stretch gap-3 xl:flex-nowrap"
        }
      >
        {showPalette && (
          <aside
            data-test="admin-flow-building-blocks"
            aria-label="Building blocks"
            className={
              fillHeight
                ? "absolute inset-y-0 left-0 z-20 w-44 shrink-0 overflow-y-auto overscroll-contain rounded border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 xl:static"
                : "w-full shrink-0 rounded border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 sm:w-44"
            }
          >
            <h3 className="mb-3 text-sm font-semibold">Building blocks</h3>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Data sources</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-1">
              {ADMIN_FLOW_SOURCE_OPTIONS.map((source) => (
                <button
                  key={source.value}
                  type="button"
                  data-test={`admin-flow-add-source-${source.value}`}
                  onClick={() => canvas.addNode("source", source.value)}
                  className="rounded border border-gray-200 p-2 text-left text-xs hover:border-green hover:bg-[#E9F4F3] focus-visible:outline focus-visible:outline-2 focus-visible:outline-green dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  <span className="font-semibold">{source.label}</span>
                  <span className="mt-1 block text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                    {source.description}
                  </span>
                </button>
              ))}
            </div>
            <p className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-gray-500">Steps</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-1">
              <button
                type="button"
                data-test="admin-flow-add-processing"
                onClick={() => canvas.addNode("agent")}
                className="rounded border border-gray-200 p-2 text-left text-xs hover:border-green hover:bg-[#E9F4F3] focus-visible:outline focus-visible:outline-2 focus-visible:outline-green dark:border-gray-600 dark:hover:bg-gray-700"
              >
                <span className="font-semibold">AI processing</span>
                <span className="mt-1 block text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                  Process connected inputs with a prompt. No tools.
                </span>
              </button>
              {ADMIN_FLOW_STEP_OPTIONS.map((step) =>
                step.kind === "agent" ? (
                  <AdminFlowToolMenu
                    key={step.kind}
                    tools={tools}
                    label={step.label}
                    allowProcessing={false}
                    description={step.description}
                    testId="admin-flow-add-agent"
                    onSelect={(tool) => canvas.addNode("agent", undefined, tool)}
                  />
                ) : (
                  <button
                    key={step.kind}
                    type="button"
                    data-test={`admin-flow-add-${step.kind}`}
                    onClick={() => canvas.addNode(step.kind)}
                    className="rounded border border-gray-200 p-2 text-left text-xs hover:border-green hover:bg-[#E9F4F3] focus-visible:outline focus-visible:outline-2 focus-visible:outline-green dark:border-gray-600 dark:hover:bg-gray-700"
                  >
                    <span className="font-semibold">{step.label}</span>
                    <span className="mt-1 block text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                      {step.description}
                    </span>
                  </button>
                ),
              )}
            </div>
            <p className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-gray-500">Outputs</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-1">
              {ADMIN_FLOW_OUTPUT_OPTIONS.map((output) => (
                <button
                  key={output.kind}
                  type="button"
                  data-test={`admin-flow-add-${output.kind}`}
                  onClick={() => canvas.addNode(output.kind)}
                  className="rounded border border-gray-200 p-2 text-left text-xs hover:border-green hover:bg-[#E9F4F3] focus-visible:outline focus-visible:outline-2 focus-visible:outline-green dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  <span className="font-semibold">{output.label}</span>
                  <span className="mt-1 block text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                    {output.description}
                  </span>
                </button>
              ))}
            </div>
          </aside>
        )}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
          <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-3 py-2 text-xs dark:border-gray-700">
            <h3 className="font-semibold">Flow canvas</h3>
            <span className="text-gray-500">
              {flow.nodes.length} blocks · {flow.edges.length} connections
            </span>
          </div>
          <div
            ref={canvas.viewportRef}
            data-test="admin-flow-canvas-viewport"
            aria-label="Flow canvas; scroll to zoom, hold Space and drag to pan"
            tabIndex={0}
            onPointerDownCapture={canvas.beginPan}
            onPointerMove={canvas.movePan}
            onPointerUp={canvas.finishPan}
            onPointerCancel={canvas.finishPan}
            onLostPointerCapture={canvas.finishPan}
            onClickCapture={canvas.ignorePanClick}
            className={
              fillHeight
                ? `relative min-h-0 flex-1 overflow-auto overscroll-contain ${canvas.panning ? "cursor-grabbing" : canvas.spacePressed ? "cursor-grab" : ""}`
                : `relative h-[560px] min-h-[360px] overflow-auto overscroll-contain ${canvas.panning ? "cursor-grabbing" : canvas.spacePressed ? "cursor-grab" : ""}`
            }
          >
            <div style={{ width: width * canvas.zoom, height: height * canvas.zoom }}>
              <div
                data-test="admin-flow-canvas"
                style={{
                  width,
                  height,
                  transform: `scale(${canvas.zoom})`,
                  transformOrigin: "0 0",
                  backgroundImage: "radial-gradient(#bfd8d3 1px, transparent 1px)",
                  backgroundSize: "20px 20px",
                }}
                className="relative touch-none"
              >
                <svg
                  width={width}
                  height={height}
                  className="pointer-events-none absolute inset-0 overflow-visible"
                  aria-hidden="true"
                >
                  <defs>
                    <marker id={markerId} markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
                      <path d="M0,0 L8,4 L0,8 Z" fill="#1F8475" />
                    </marker>
                  </defs>
                  {flow.edges.map((edge) => {
                    const from = nodeIndex.get(edge.from);
                    const to = nodeIndex.get(edge.to);

                    if (!from || !to) {
                      return null;
                    }

                    const startX = from.x + ADMIN_FLOW_CARD_WIDTH;
                    const startY = from.y + ADMIN_FLOW_CARD_HEIGHT / 2;
                    const endX = to.x;
                    const endY = to.y + ADMIN_FLOW_CARD_HEIGHT / 2;
                    const curve = Math.max(48, Math.abs(endX - startX) / 2);

                    return (
                      <path
                        key={edge.id}
                        data-test={`admin-flow-edge-${edge.id}`}
                        d={`M ${startX} ${startY} C ${startX + curve} ${startY}, ${endX - curve} ${endY}, ${endX} ${endY}`}
                        fill="none"
                        stroke="#1F8475"
                        strokeWidth={2}
                        markerEnd={`url(#${markerId})`}
                      />
                    );
                  })}
                </svg>
                {!flow.nodes.length && (
                  <div className="pointer-events-none absolute left-8 top-16 max-w-sm rounded border border-dashed border-gray-300 bg-white/90 p-6 text-sm dark:border-gray-600 dark:bg-gray-800/90">
                    <p className="font-semibold">Start with a data source</p>
                    <p className="mt-2 leading-6 text-gray-500">
                      Add a vessel search, document search or approved inbox. Connect it to an AI tool and finish with
                      an output.
                    </p>
                  </div>
                )}
                {flow.nodes.map((node) => {
                  const toolNames = getAdminFlowToolNames(node);
                  const isSelected = node.id === canvas.selectedNode?.id;
                  const source = ADMIN_FLOW_SOURCE_OPTIONS.find((option) => option.value === node.source);
                  const detail =
                    node.kind === "source"
                      ? source?.label || "Select a data source"
                      : node.kind === "agent"
                        ? toolNames.length > 1
                          ? `${toolNames.length} attached tools`
                          : toolNames[0] || "Analyze connected inputs"
                        : node.kind === "merge"
                          ? "All inputs required"
                          : node.kind === "email"
                            ? "Review before sending"
                            : "Text response";

                  return (
                    <article
                      key={node.id}
                      data-test={`admin-flow-node-${node.id}`}
                      style={{
                        left: node.x,
                        top: node.y,
                        width: ADMIN_FLOW_CARD_WIDTH,
                        height: ADMIN_FLOW_CARD_HEIGHT,
                      }}
                      className={`absolute rounded border border-l-[3px] bg-white shadow-sm dark:bg-gray-800 ${ADMIN_FLOW_KIND_COLORS[node.kind]} ${isSelected ? "z-10 border-green ring-2 ring-green/25" : "border-gray-200 dark:border-gray-600"}`}
                    >
                      <button
                        type="button"
                        data-test={`admin-flow-node-select-${node.id}`}
                        onClick={() => {
                          if (canvas.spacePressed) return;
                          canvas.setSelectedId(node.id);
                          setShowSettings(true);
                        }}
                        onPointerDown={(event) => canvas.beginNodeDrag(event, node)}
                        onPointerMove={canvas.moveNode}
                        onPointerUp={canvas.finishNodeDrag}
                        onPointerCancel={canvas.finishNodeDrag}
                        onKeyDown={(event) => canvas.moveNodeWithKeyboard(event, node)}
                        aria-pressed={isSelected}
                        title="Select settings; drag or use arrow keys to move"
                        className="flex h-full w-full cursor-grab touch-none flex-col items-start justify-center gap-2 overflow-hidden rounded px-4 py-3 text-left text-xs outline-none focus-visible:ring-2 focus-visible:ring-green active:cursor-grabbing"
                      >
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          {node.kind === "agent" && !toolNames.length
                            ? "AI processing"
                            : ADMIN_FLOW_KIND_LABELS[node.kind]}
                        </span>
                        <span className="w-full truncate font-semibold">{node.label || "Unnamed block"}</span>
                        <span className="w-full truncate text-[11px] text-gray-500 dark:text-gray-400">{detail}</span>
                      </button>
                      <button
                        type="button"
                        data-test={`admin-flow-input-${node.id}`}
                        aria-label={`Connect into ${node.label || "block"}`}
                        title="Input port"
                        onPointerUp={(event) => {
                          event.stopPropagation();
                          if (canvas.connectingFrom) canvas.finishConnection(node.id);
                        }}
                        onClick={(event) => {
                          if (event.detail === 0) canvas.finishConnection(node.id);
                        }}
                        className="absolute -left-2 top-1/2 z-20 h-4 w-4 -translate-y-1/2 rounded-full border-2 border-green bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-green dark:bg-gray-800"
                      />
                      <button
                        type="button"
                        data-test={`admin-flow-output-${node.id}`}
                        aria-label={`Connect from ${node.label || "block"}`}
                        title="Output port"
                        onPointerDown={(event) => {
                          event.stopPropagation();
                          canvas.setConnectingFrom(node.id);
                          canvas.setError("");
                        }}
                        onClick={() => {
                          canvas.setConnectingFrom(node.id);
                          canvas.setError("");
                        }}
                        className={`absolute -right-2 top-1/2 z-20 h-4 w-4 -translate-y-1/2 rounded-full border-2 border-green focus-visible:outline focus-visible:outline-2 focus-visible:outline-green ${canvas.connectingFrom === node.id ? "bg-green" : "bg-white dark:bg-gray-800"}`}
                      />
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        {showSettings && (
          <aside
            data-test="admin-flow-settings-pane"
            aria-label="Block settings"
            className={
              fillHeight
                ? "absolute inset-y-0 right-0 z-20 w-72 shrink-0 overflow-y-auto overscroll-contain rounded border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 xl:static"
                : "max-h-[760px] w-full shrink-0 overflow-y-auto rounded border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:w-72"
            }
          >
            {canvas.selectedNode ? (
              <>
                <AdminFlowBlockSettings
                  key={canvas.selectedNode.id}
                  node={canvas.selectedNode}
                  tools={tools}
                  onChange={canvas.updateNode}
                  onDelete={canvas.deleteNode}
                />
                <div className="mt-5 border-t border-gray-200 pt-4 dark:border-gray-700">
                  <h4 className="mb-2 text-xs font-semibold">Connections</h4>
                  {!selectedConnections.length && (
                    <p className="text-xs text-gray-500">No connected inputs or outputs yet.</p>
                  )}
                  <ul className="space-y-2">
                    {selectedConnections.map((edge) => (
                      <li key={edge.id} className="flex items-center justify-between gap-2 text-xs">
                        <span className="min-w-0 truncate">
                          {nodeIndex.get(edge.from)?.label || "Block"} → {nodeIndex.get(edge.to)?.label || "Block"}
                        </span>
                        <button
                          type="button"
                          data-test={`admin-flow-disconnect-${edge.id}`}
                          aria-label={`Disconnect ${nodeIndex.get(edge.from)?.label || "block"} from ${nodeIndex.get(edge.to)?.label || "block"}`}
                          onClick={() =>
                            onChange({ ...flow, edges: flow.edges.filter((entry) => entry.id !== edge.id) })
                          }
                          className="shrink-0 text-red-700 underline dark:text-red-300"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            ) : (
              <div className="text-sm">
                <h3 className="font-semibold">Block settings</h3>
                <p className="mt-3 text-xs leading-5 text-gray-500">
                  Select a block to configure its source, tool, instructions or output. Each incoming connection
                  supplies context to that step.
                </p>
              </div>
            )}
          </aside>
        )}
      </div>
    </section>
  );
};

export default AdminFlowWhiteboard;
